import React from 'react';
import { Progress } from '@components/atoms/Progress';
import { HealthPill } from '@components/molecules/HealthPill';
import { ClientProjectOverview } from '@models/client.model';
import { SubcontractorHealth } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';
import { ClientCounter } from './ClientCounter';
import {
  completionPercent,
  healthBarVariant,
  nonZeroCounters,
} from './clientProgressFormat';
import { fillTemplate } from './clientProjectDisplay';

interface ClientProgressStripProps {
  /** `undefined` while the overview is loading (skeleton). */
  overview?: ClientProjectOverview;
  isLoading?: boolean;
}

/**
 * Sprint 44, story-226. The progress strip on a client home card: health pill, bar,
 * "{complete} of {countable} tasks", then only the non-zero counters. Rendered from the server's
 * overview row; a project with nothing wrong shows the pill and the bar and nothing else. When
 * the overview failed (`overview` undefined, not loading) the strip is omitted entirely.
 */
export const ClientProgressStrip: React.FC<ClientProgressStripProps> = ({
  overview,
  isLoading = false,
}) => {
  const t = useTranslation('clientProgress');

  if (!overview) {
    return isLoading ? (
      <div
        data-testid="client-progress-strip-skeleton"
        className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-600 flex items-center gap-3 animate-pulse">
        <div className="h-5 w-20 rounded-full bg-gray-200 dark:bg-gray-600" />
        <div className="h-2 flex-1 rounded-full bg-gray-200 dark:bg-gray-600" />
      </div>
    ) : null;
  }

  const hasBar =
    overview.health !== SubcontractorHealth.NONE && overview.countableCount > 0;
  const counters = nonZeroCounters(overview);

  return (
    <div
      data-testid="client-progress-strip"
      className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-600 flex flex-col gap-2">
      <div className="flex items-center gap-3">
        <HealthPill health={overview.health} context="client" size="sm" />
        {hasBar && (
          <>
            <div
              className="flex-1 min-w-[4rem]"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={completionPercent(overview.completion)}
              aria-label={fillTemplate(t.tasksOfTotal, {
                complete: overview.completeCount,
                countable: overview.countableCount,
              })}>
              <Progress
                size="sm"
                value={completionPercent(overview.completion)}
                variant={healthBarVariant(overview.health)}
              />
            </div>
            <span className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
              {fillTemplate(t.tasksOfTotal, {
                complete: overview.completeCount,
                countable: overview.countableCount,
              })}
            </span>
          </>
        )}
      </div>
      {counters.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {counters.map(({ kind, count }) => (
            <ClientCounter
              key={kind}
              kind={kind}
              count={count}
              variant="chip"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientProgressStrip;
